import React from 'react';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Seven-segment patterns: [top, top-right, bottom-right, bottom, bottom-left, top-left, middle]
const SEGMENTS = {
  '0': [1, 1, 1, 1, 1, 1, 0],
  '1': [0, 1, 1, 0, 0, 0, 0],
  '2': [1, 1, 0, 1, 1, 0, 1],
  '3': [1, 1, 1, 1, 0, 0, 1],
  '4': [0, 1, 1, 0, 0, 1, 1],
  '5': [1, 0, 1, 1, 0, 1, 1],
  '6': [1, 0, 1, 1, 1, 1, 1],
  '7': [1, 1, 1, 0, 0, 0, 0],
  '8': [1, 1, 1, 1, 1, 1, 1],
  '9': [1, 1, 1, 1, 0, 1, 1],
};

// A single 7-segment digit rendered as SVG.
// viewBox 40x70, segments built from rectangles/polygons.
function SegmentDigit({ digit, on, off, glow }) {
  const pattern = SEGMENTS[digit] || SEGMENTS['0'];
  const color = (i) => pattern[i] ? on : off;
  const filter = (i) => pattern[i] && glow ? `drop-shadow(0 0 6px ${on})` : undefined;

  // Segment coordinates — trapezoidal segments with a gap between them.
  const horiz = (cx, cy) => {
    const w = 22, h = 6;
    return `M${cx - w / 2 + h / 2},${cy} L${cx - w / 2 + h},${cy - h / 2} L${cx + w / 2 - h},${cy - h / 2} L${cx + w / 2 - h / 2},${cy} L${cx + w / 2 - h},${cy + h / 2} L${cx - w / 2 + h},${cy + h / 2} Z`;
  };
  const vert = (cx, cy) => {
    const w = 6, h = 26;
    return `M${cx},${cy - h / 2 + w / 2} L${cx + w / 2},${cy - h / 2 + w} L${cx + w / 2},${cy + h / 2 - w} L${cx},${cy + h / 2 - w / 2} L${cx - w / 2},${cy + h / 2 - w} L${cx - w / 2},${cy - h / 2 + w} Z`;
  };

  return (
    <svg viewBox="0 0 40 72" className="w-12 h-20 md:w-16 md:h-28">
      {/* a = top */}       <path d={horiz(20, 6)}  fill={color(0)} style={{ filter: filter(0) }} />
      {/* b = top-right */} <path d={vert(34, 20)}  fill={color(1)} style={{ filter: filter(1) }} />
      {/* c = bot-right */} <path d={vert(34, 52)}  fill={color(2)} style={{ filter: filter(2) }} />
      {/* d = bottom */}    <path d={horiz(20, 66)} fill={color(3)} style={{ filter: filter(3) }} />
      {/* e = bot-left */}  <path d={vert(6, 52)}   fill={color(4)} style={{ filter: filter(4) }} />
      {/* f = top-left */}  <path d={vert(6, 20)}   fill={color(5)} style={{ filter: filter(5) }} />
      {/* g = middle */}    <path d={horiz(20, 36)} fill={color(6)} style={{ filter: filter(6) }} />
    </svg>
  );
}

function Colon({ on, off, glow, blink }) {
  const fill = blink ? on : off;
  const filter = blink && glow ? `drop-shadow(0 0 6px ${on})` : undefined;
  return (
    <svg viewBox="0 0 14 72" className="w-3 md:w-5 h-20 md:h-28">
      <circle cx="7" cy="24" r="4" fill={fill} style={{ filter }} />
      <circle cx="7" cy="48" r="4" fill={fill} style={{ filter }} />
    </svg>
  );
}

export default function SegmentClock({ time, settings, dark, palette }) {
  const accent = palette?.[0] || '#ef4444';
  const off = dark ? 'rgba(255,255,255,0.06)' : 'rgba(31,41,55,0.08)';

  let hours = time.getHours();
  const isPM = hours >= 12;
  hours = hours % 12 || 12;
  const hh = String(hours).padStart(2, '0');
  const mm = String(time.getMinutes()).padStart(2, '0');
  const ss = String(time.getSeconds()).padStart(2, '0');
  const blink = time.getSeconds() % 2 === 0;

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="flex items-center gap-1 md:gap-2">
        <SegmentDigit digit={hh[0]} on={accent} off={off} glow />
        <SegmentDigit digit={hh[1]} on={accent} off={off} glow />
        <Colon on={accent} off={off} glow blink={blink} />
        <SegmentDigit digit={mm[0]} on={accent} off={off} glow />
        <SegmentDigit digit={mm[1]} on={accent} off={off} glow />
        {settings.showSeconds && (
          <>
            <Colon on={accent} off={off} glow blink={blink} />
            <SegmentDigit digit={ss[0]} on={accent} off={off} glow />
            <SegmentDigit digit={ss[1]} on={accent} off={off} glow />
          </>
        )}
      </div>
      <div className={`text-sm font-mono tracking-[0.3em] ${dark ? 'text-white/40' : 'text-gray-500/70'}`}>
        {isPM ? 'PM' : 'AM'}
      </div>
      {settings.showDate && (
        <div className={`text-base font-rasa ${dark ? 'text-white/50' : 'text-gray-500/70'}`}>
          {DAYS[time.getDay()]}, {MONTHS[time.getMonth()]} {time.getDate()}, {time.getFullYear()}
        </div>
      )}
    </div>
  );
}
